import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { usePlatformDashboardSelector } from 'platform-dashboard-ui/store';
import { RemoveIcon } from 'foundations-assets';
import { List } from 'immutable';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UIFlex from 'UIComponents/layout/UIFlex';
import { Popover } from 'foundations-components/transitional/components/Popover';
import UIIcon from 'UIComponents/icon/UIIcon';
import FormattedMessage from 'I18n/components/FormattedMessage';
import DashboardFilterOperatorInput from '../dashboardFilters/controls/DashboardFilterOperatorInput';
import PinnedPropertyPopoverButton from './shared/PinnedPropertyPopoverButton';
import UITruncateString from 'UIComponents/text/UITruncateString';
import { getLabel, getName, getAccessLevel
// @ts-expect-error migrate upstream dependency
} from '../../../../lib/dashboardFilters/propertySchema';
// @ts-expect-error migrate upstream dependency
import { getFullyQualifiedName } from '../../../../lib/dashboardFilters/dataSourceMetadataSchema';
import { getDefaultOperatorForProperty } from 'platform-dashboard-ui/filter/utils/dataSourceHelpers';
import { RESTRICTED_FLP } from 'platform-dashboard-ui/filter/data/DataSourcePropertiesDAO';
import { addDashboardFilter, updateDashboardFilter, selectDashboardFilters, removeDashboardFilterByName } from '../../../../ducks/dashboardFilters';
import { selectDataSourcesMetadataWithUniqueLabels } from 'platform-dashboard-ui/ducks/dataSourceMetadataSlice';
// @ts-expect-error migrate upstream dependency
import { getLabel as getDataSourceLabel } from '../../../../lib/dashboardFilters/dataSourceMetadataSchema';
import { track, EVENTS, trackFilterInteraction
// @ts-expect-error migrate upstream dependency
} from '../../../../../tracking/DashboardUITracker';
import { View } from 'foundations-components/components/View';
import { selectHasUnifiedFilterDashboard } from 'dashboard-lib/public/userInfo/gates';
import { PinnedPropertyCRMInput } from './PinnedPropertyCRMFilter';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const POPOVER_WIDTH = '400';
export const PinnedPropertyFilterLabelContainer = ({
  children,
  showCarat = false
}) => {
  return /*#__PURE__*/_jsxs(UIFlex, {
    align: "center",
    gap: 3,
    justify: "between",
    children: [/*#__PURE__*/_jsx(UIFlex, {
      align: "start",
      gap: 1,
      justify: "between",
      direction: showCarat ? 'row' : 'column',
      children: children
    }), showCarat && /*#__PURE__*/_jsx(UIIcon, {
      name: "downCarat"
    })]
  });
};
const PinnedPropertyCounter = ({
  operator
}) => {
  if (!operator) {
    return null;
  }
  const operatorValue = operator.get('value');
  const isList = List.isList(operatorValue);
  if (!isList) {
    return null;
  }
  return /*#__PURE__*/_jsxs(View, {
    marginInlineEnd: "100",
    as: "span",
    children: ["(", operatorValue.size, ")"]
  });
};
const PinnedPropertyFilter = ({
  property,
  dataSource,
  prevOperator,
  dashboardId,
  userHasQuickFilterEditPermission,
  userRole
}) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const propertyAccessRestricted = getAccessLevel(property) === RESTRICTED_FLP;
  const displayName = getLabel(property);
  const propertyName = getName(property);
  const appliedFilters = useSelector(selectDashboardFilters(dashboardId));
  const dataSourceMetadata = usePlatformDashboardSelector(selectDataSourcesMetadataWithUniqueLabels([dataSource]));
  const hasUnifiedFilterDashboard = useSelector(selectHasUnifiedFilterDashboard);
  if (!dataSourceMetadata[dataSource]) {
    return null;
  }
  const dataSourceLabel = getDataSourceLabel(dataSourceMetadata[dataSource]);
  const currentAppliedOperator = prevOperator || getDefaultOperatorForProperty(dataSource, property);
  const appliedFilterIndex = appliedFilters.findIndex(filter => filter.propertyName === propertyName && filter.dataSource === dataSource);
  const onApplyFilter = updatedFilter => {
    if (!updatedFilter) {
      return;
    }
    const filterData = {
      dataSource,
      dataSourceName: getFullyQualifiedName(dataSourceMetadata[dataSource]),
      operator: updatedFilter,
      property
    };
    if (appliedFilterIndex === -1) {
      dispatch(addDashboardFilter({
        dashboardId,
        filterData,
        dataSource,
        property
      }));
      trackFilterInteraction({
        action: 'add-filter',
        filterData,
        userRole,
        additionalProps: {
          interactionSource: 'quick-filter-popover',
          dashboardId
        }
      });
    } else {
      dispatch(updateDashboardFilter({
        dashboardId,
        index: appliedFilterIndex,
        filterData
      }));
      trackFilterInteraction({
        action: 'edit-filter',
        filterData,
        userRole,
        additionalProps: {
          interactionSource: 'quick-filter-popover',
          dashboardId
        }
      });
    }
    setOpen(false);
  };
  const handleTogglePopover = () => {
    setOpen(!open);
    track(EVENTS.toggleQuickFilterPopover, {
      actionType: 'explore',
      action: open ? 'close-quick-filter-popover' : 'open-quick-filter-popover',
      assetType: 'dashboard',
      propertyDisplayName: displayName,
      propertyName,
      dataSourceLabel,
      dataSourceId: dataSource
    });
  };
  return /*#__PURE__*/_jsx(Popover, {
    open: open,
    arrowSize: "small",
    autoPlacement: true,
    onOpenChange: handleTogglePopover,
    placement: "bottom",
    inlineSize: POPOVER_WIDTH,
    content: {
      body: hasUnifiedFilterDashboard ? /*#__PURE__*/_jsx(PinnedPropertyCRMInput, {
        dataSource: dataSource,
        onApplyFilter: onApplyFilter,
        property: property,
        prevOperator: currentAppliedOperator,
        userHasQuickFilterEditPermission: userHasQuickFilterEditPermission,
        dashboardId: dashboardId
      }) : /*#__PURE__*/_jsx(DashboardFilterOperatorInput, {
        prevOperator: currentAppliedOperator,
        property: property,
        dataSource: dataSource,
        userHasQuickFilterEditPermission: userHasQuickFilterEditPermission,
        dashboardId: dashboardId,
        onApplyFilter: onApplyFilter,
        isQuickFilterInput: true
      })
    },
    children: /*#__PURE__*/_jsx(UITooltip, {
      disabled: !propertyAccessRestricted,
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "dashboardHeader.dashboardFilters.allFilters.panel.tooltip.propertyAccessRestricted"
      }),
      children: /*#__PURE__*/_jsxs(PinnedPropertyPopoverButton, {
        dataTestId: `${propertyName}-quick-filter-popover-button`,
        onClick: handleTogglePopover,
        prevOperator: prevOperator,
        disabled: propertyAccessRestricted,
        isOpen: open,
        children: [/*#__PURE__*/_jsxs(PinnedPropertyFilterLabelContainer, {
          showCarat: true,
          children: [/*#__PURE__*/_jsx(PinnedPropertyCounter, {
            operator: currentAppliedOperator
          }), /*#__PURE__*/_jsx(UITruncateString, {
            useFlex: true,
            maxWidth: 150,
            tooltip: `${dataSourceLabel}: ${displayName}`,
            children: displayName
          })]
        }), prevOperator && /*#__PURE__*/_jsx(UITooltip, {
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "propertyList.filterControl.clear"
          }),
          children: /*#__PURE__*/_jsx(IconHolder, {
            icon: RemoveIcon,
            padding: "0",
            color: "icon-core-subtle",
            "data-test-id": "unapply-quick-filter-button",
            onClick: e => {
              e.stopPropagation();
              dispatch(removeDashboardFilterByName({
                dashboardId,
                dataSource,
                propertyName
              }));
              trackFilterInteraction({
                action: 'delete-filter',
                filterData: {
                  dataSource,
                  dataSourceName: getFullyQualifiedName(dataSourceMetadata[dataSource]),
                  operator: prevOperator,
                  property
                },
                userRole,
                additionalProps: {
                  interactionSource: 'quick-filter-popover',
                  dashboardId
                }
              });
            }
          })
        })]
      })
    })
  });
};
export default PinnedPropertyFilter;